import React, { useState, useEffect, useCallback, useMemo } from "react";
import Button from "../Button";

const Chevron = ({ open }) => (
  <svg
    className={`h-4 w-4 transition-transform duration-200 ${
      open ? "rotate-180" : ""
    }`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export default function CategoryFilters({
  filtersData,
  onChange,
  onClear,
  isLoading = false,
  onApply,
}) {
  const [expandedFilters, setExpandedFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});

  // پیش‌فرض بسته بودن منو در موبایل
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const initialFilters = useMemo(() => {
    if (!filtersData?.filters) return {};

    const initial = {};
    filtersData.filters.forEach((filter) => {
      if (filter.type === "number") {
        initial[filter.key] = {
          min: filter.min ?? 0,
          max: filter.max ?? 0,
        };
      } else if (filter.type === "select") {
        initial[filter.key] = [];
      }
    });
    return initial;
  }, [filtersData?.filters]);

  useEffect(() => {
    if (!filtersData?.filters) return;

    setSelectedFilters(initialFilters);

    const expanded = {};
    filtersData.filters.forEach((f) => {
      expanded[f.key] = false;
    });
    setExpandedFilters(expanded);
  }, [initialFilters, filtersData?.filters]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");

    const handleChange = () => setIsMobile(mediaQuery.matches);
    handleChange();

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const activeFilters = useMemo(() => {
    const result = {};

    Object.entries(selectedFilters).forEach(([key, value]) => {
      const base = initialFilters[key];
      if (!base) return;

      if (Array.isArray(value) && value.length > 0) {
        result[key] = value;
        return;
      }

      if (
        value &&
        typeof value === "object" &&
        ("min" in value || "max" in value)
      ) {
        if (value.min !== base.min || value.max !== base.max) {
          result[key] = value;
        }
      }
    });

    return result;
  }, [selectedFilters, initialFilters]);

  useEffect(() => {
    onChange?.(activeFilters);
  }, [activeFilters, onChange]);

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const handleNumberChange = useCallback((key, field, value) => {
    const num = Number(value) || 0;
    setSelectedFilters((prev) => {
      const current = prev[key];
      const next = { ...current, [field]: num };

      if (next.min > next.max) {
        field === "min" ? (next.max = next.min) : (next.min = next.max);
      }

      return { ...prev, [key]: next };
    });
  }, []);

  const handleSelectChange = useCallback((key, option) => {
    setSelectedFilters((prev) => {
      const current = prev[key];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [key]: updated };
    });
  }, []);

  const handleClear = useCallback(() => {
    setSelectedFilters(initialFilters);
    onClear?.();
    setMobileOpen(false); // بستن منو بعد از حذف فیلترها
  }, [initialFilters, onClear]);

  const handleApply = useCallback(() => {
    onApply?.();
    setMobileOpen(false); // بستن منو بعد از اعمال فیلتر
  }, [onApply]);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const toggleAccordion = useCallback((key) => {
    setExpandedFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // جلوگیری از اسکرول body هنگام باز بودن منو در موبایل
  useEffect(() => {
    if (isMobile && mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isMobile, mobileOpen]);

  if (!filtersData?.filters?.length) return null;

  return (
      <>
        {/* دکمه باز کردن فقط در موبایل */}
        <div className="lg:hidden flex-1">
          <button
              type="button"
              onClick={openMobile}
              className="flex w-full items-center bg-white justify-first gap-2 rounded-lg border-b border-gray-200 px-4 py-2.5 text-xm font-medium text-gray-600 transition-colors hover:bg-[#DBDBDB] active:bg-[#CECECE]"
          >
            <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
            >
              <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4h18M3 12h18M3 20h18"
              />
            </svg>
            <span>فیلترها</span>
          </button>
        </div>


        {/* Overlay موبایل */}
        {isMobile && mobileOpen && (
            <div
                className="fixed inset-0 z-40 bg-black/40 lg:hidden"
                onClick={closeMobile}
            />
        )}

        {/* پنل فیلتر */}
        <aside
            className={`fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col bg-white shadow-xl transition-transform duration-300 lg:static lg:z-auto lg:h-auto lg:w-[320px] lg:shrink-0 lg:translate-x-0 lg:rounded-2xl lg:border lg:border-gray-200 lg:shadow-sm ${
                isMobile
                    ? mobileOpen
                        ? "translate-x-0"
                        : "-translate-x-full"
                    : "translate-x-0"
            }`}
        >
          {/* هدر ثابت در موبایل */}
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <h3 className="text-base font-semibold text-gray-800">
                فیلتر محصولات
              </h3>

              {/* دکمه بستن منو */}
              <button
                  type="button"
                  onClick={closeMobile}
                  aria-label="بستن فیلترها"
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-700 transition hover:border-[#E74C3C] hover:text-[#E74C3C] lg:hidden"
              >
                <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                >
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* دکمه‌های ثابت موبایل */}
            <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 lg:hidden">
              <button
                  type="button"
                  onClick={handleApply}
                  disabled={!hasActiveFilters}
                  className="flex-1 rounded-lg bg-[#1E3A5F] px-4 py-2 text-sm font-medium text-[#FFC107] transition hover:bg-[#2B5388] disabled:cursor-not-allowed disabled:opacity-50"
              >
                اعمال فیلتر
              </button>

              <button
                  type="button"
                  onClick={handleClear}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-[#E74C3C] hover:text-[#E74C3C]"
              >
                حذف فیلترها
              </button>
            </div>
          </div>

          {/* محتوای اسکرول‌پذیر */}
          <div className="flex-1 overflow-y-auto px-4 py-4 mb-[48px]">
            {isLoading ? (
                <div className="space-y-4">
                  <div className="h-5 w-1/2 rounded bg-gray-200 animate-pulse"/>
                  <div className="h-11 rounded-lg bg-gray-200 animate-pulse"/>
                  <div className="h-11 rounded-lg bg-gray-200 animate-pulse"/>
                  <div className="h-11 rounded-lg bg-gray-200 animate-pulse"/>
                </div>
            ) : (
                <div className="space-y-3">
                  {filtersData.filters.map((filter) => (
                      <div
                          key={filter.key}
                          className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                      >
                        {filter.type === "select" ? (
                            <button
                                type="button"
                                onClick={() => toggleAccordion(filter.key)}
                                className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-right transition hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <Chevron open={expandedFilters[filter.key]}/>
                                <span className="text-sm font-medium text-gray-800">
                          {filter.label}
                        </span>
                              </div>
                            </button>
                        ) : (
                            <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-800">
                        {filter.label}
                      </span>
                              {filter.unit && (
                                  <span className="text-xs text-gray-500">
                          {filter.unit}
                        </span>
                              )}
                            </div>
                        )}

                        {filter.type === "number" && (
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                  type="number"
                                  value={selectedFilters[filter.key]?.min ?? ""}
                                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-700 outline-none transition focus:border-[#1E3A5F]"
                                  onChange={(e) =>
                                      handleNumberChange(filter.key, "min", e.target.value)
                                  }
                              />
                              <input
                                  type="number"
                                  value={selectedFilters[filter.key]?.max ?? ""}
                                  className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-700 outline-none transition focus:border-[#1E3A5F]"
                                  onChange={(e) =>
                                      handleNumberChange(filter.key, "max", e.target.value)
                                  }
                              />
                            </div>
                        )}

                        {filter.type === "select" &&
                            expandedFilters[filter.key] && (
                                <div className="mt-3 space-y-1">
                                  {filter.options?.map((opt) => (
                                      <label
                                          key={opt.value}
                                          className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 transition hover:bg-gray-50"
                                      >
                                        <div className="flex items-center gap-2">
                                          <input
                                              type="checkbox"
                                              checked={selectedFilters[filter.key]?.includes(
                                                  opt.value
                                              )}
                                              onChange={() =>
                                                  handleSelectChange(filter.key, opt.value)
                                              }
                                              className="h-4 w-4 rounded border-gray-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                                          />
                                          <span className="text-sm text-gray-700">
                                {opt.label}
                              </span>
                                        </div>

                                        <span className="text-xs text-gray-500">
                              {opt.count}
                            </span>
                                      </label>
                                  ))}
                                </div>
                            )}
                      </div>
                  ))}
                </div>
            )}
          </div>

          {/* دکمه‌ها در دسکتاپ داخل پنل و در موبایل مخفی */}
          <div className="hidden border-t border-gray-200 bg-white px-4 py-4 lg:block">
            <div className="flex flex-col gap-3">
              <Button variant="primary" onClick={onApply} disabled={!hasActiveFilters}>
                اعمال فیلتر
              </Button>

              <Button variant="secondary" onClick={handleClear}>
                حذف فیلترها
              </Button>
            </div>
          </div>
        </aside>
      </>
  );
}
